// ListenInPlayer — Day 5. Streams μ-law audio from the AI Dialer bridge into
// a Web Audio context so the agent can hear the live call.
//
// Wire: server sends binary messages of the form  [hdr][μ-law payload]
//   hdr == 0x01 → inbound (lead → AI)
//   hdr == 0x02 → outbound (AI → lead)
//
// Both directions are decoded and mixed into a single output. Read-only —
// the agent has no mic and cannot speak into the call.

import React, { useEffect, useRef, useState } from 'react';
import { Volume2, X, Loader2 } from 'lucide-react';

// μ-law → PCM16 LUT, built once.
function buildMulawLut() {
  const lut = new Int16Array(256);
  for (let i = 0; i < 256; i++) {
    const u = ~i & 0xff;
    const sign = u & 0x80;
    let exponent = (u >> 4) & 0x07;
    let mantissa = u & 0x0f;
    let sample = ((mantissa << 3) + 0x84) << exponent;
    sample -= 0x84;
    lut[i] = sign ? -sample : sample;
  }
  return lut;
}
const MULAW_LUT = buildMulawLut();

function mulawToFloat32(ulaw) {
  const out = new Float32Array(ulaw.length);
  for (let i = 0; i < ulaw.length; i++) {
    out[i] = MULAW_LUT[ulaw[i]] / 32768;
  }
  return out;
}

export default function ListenInPlayer({ leadName, callId, onClose }) {
  const [status, setStatus] = useState('connecting'); // connecting | playing | error | closed
  const [error, setError] = useState('');
  const wsRef = useRef(null);
  const ctxRef = useRef(null);
  const nextStartRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let ws;

    async function start() {
      try {
        // Mint a token + WS URL.
        const { apiPost } = await import('../../lib/api.js');
        const r = await apiPost(`/dialer/v1/calls/${callId}/listen-in`, {});
        if (cancelled) return;
        const { token } = r?.data || {};
        if (!token) throw new Error('No token returned');

        // WebSocket URL. Convert https→wss / http→ws based on current origin
        // when API is same-origin; otherwise use VITE_API_URL.
        const apiBase = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '');
        const isAbs = /^https?:\/\//i.test(apiBase);
        let wsUrl;
        if (isAbs) {
          wsUrl = apiBase.replace(/^http/i, 'ws') + `/dialer/v1/ws/listen/${callId}?token=${encodeURIComponent(token)}`;
        } else {
          const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
          wsUrl = `${proto}//${window.location.host}${apiBase}/dialer/v1/ws/listen/${callId}?token=${encodeURIComponent(token)}`;
        }

        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioCtx({ sampleRate: 8000 });
        ctxRef.current = ctx;
        // Some browsers require resume after user gesture; this whole flow
        // runs from a click so resume is safe.
        try { await ctx.resume(); } catch (_) {}

        ws = new WebSocket(wsUrl);
        ws.binaryType = 'arraybuffer';
        wsRef.current = ws;

        ws.onopen = () => { if (!cancelled) setStatus('playing'); };
        ws.onerror = () => {
          if (!cancelled) { setStatus('error'); setError('Connection lost'); }
        };
        ws.onclose = () => {
          if (!cancelled) setStatus('closed');
        };
        ws.onmessage = (ev) => {
          if (!(ev.data instanceof ArrayBuffer)) return;
          const data = new Uint8Array(ev.data);
          if (data.length < 2) return;
          // Drop the header byte (direction). For Day 5 we mix both
          // directions into the same output — the user just needs to hear
          // the conversation.
          const ulaw = data.subarray(1);
          const pcm = mulawToFloat32(ulaw);
          schedulePlayback(pcm);
        };

        function schedulePlayback(pcm) {
          const cur = ctxRef.current;
          if (!cur || cur.state === 'closed') return;
          const buf = cur.createBuffer(1, pcm.length, 8000);
          buf.getChannelData(0).set(pcm);
          const src = cur.createBufferSource();
          src.buffer = buf;
          src.connect(cur.destination);
          const startAt = Math.max(cur.currentTime + 0.05, nextStartRef.current);
          src.start(startAt);
          nextStartRef.current = startAt + buf.duration;
        }
      } catch (e) {
        if (!cancelled) {
          setStatus('error');
          setError(e?.response?.data?.error || e?.message || 'Failed to listen in');
        }
      }
    }

    start();
    return () => {
      cancelled = true;
      try { ws?.close?.(); } catch (_) {}
      wsRef.current = null;
      // Guard: only close if AudioContext is not already closed.
      // React 18 strict-mode may run this cleanup twice; the second run
      // must be a no-op to avoid "Cannot close a closed AudioContext".
      if (ctxRef.current && ctxRef.current.state !== 'closed') {
        try { ctxRef.current.close(); } catch (_) { /* already closed */ }
      }
      ctxRef.current = null;
    };
  }, [callId]);

  return (
    <div className="fixed bottom-4 right-4 z-40 panel p-3 flex items-center gap-3 bg-civix-purple/20 border-civix-purple/60 shadow-lg">
      {status === 'connecting' && <Loader2 className="w-4 h-4 text-civix-purple animate-spin" />}
      {status === 'playing' && <Volume2 className="w-4 h-4 text-civix-cyan animate-pulse" />}
      {status === 'error' && <span className="text-xs text-red-300">!</span>}
      <div className="text-sm">
        <div className="text-white">
          {status === 'playing' ? `Listening to ${leadName || 'live call'}` :
            status === 'error' ? `Listen-in error: ${error}` :
            status === 'closed' ? 'Listen-in ended' : 'Connecting…'}
        </div>
        <div className="text-xs text-white/50">Muted listener — you cannot speak.</div>
      </div>
      <button
        onClick={onClose}
        aria-label="Close listen-in"
        title="Close listen-in"
        className="ml-2 text-white/60 hover:text-white"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
